import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import { ICON_NOTICE_RED } from 'assets/icons';
import CommonImage from 'components/common/CommonImage';
import { ScheduleParam } from 'containers/ProductRegist/hooks/types';
import { DateFormat } from '../hooks/useDateAdditionalHooks';
import { ONLY_NUMBER_REGEX } from 'utils/regExp';

dayjs.extend(duration);

const DueDateContainer = styled.div<{
  $isSelected: boolean;
  $isDisable?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  position: relative;

  padding: 12px;
  width: 100%;

  border-radius: 12px;
  border: ${props =>
    props.$isSelected ? '1.5px solid #414B56' : '1px solid #eaebec'};
  background: ${props =>
    props.$isDisable ? '#F7F7F8' : props.$isSelected ? '#F7F7F8' : '#fff'};

  cursor: pointer;
`;

const SelectDateForm = styled.button<{ $isDisable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 16px 12px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #dbdcdf;
  background: ${props => (props.$isDisable ? '#F7F7F8' : '#fff')};

  input {
    background: inherit;
    &::placeholder {
      color: #c2c2c8;
    }
  }

  &:focus-within {
    border: ${props =>
      props.$isDisable ? '1px solid #dbdcdf' : '1.5px solid #414b56'};
  }
`;

const DATE_FORMAT_LIST = [
  {
    text: '1일 전 까지',
    value: '1day',
  },
  {
    text: '2일 전 까지',
    value: '2day',
  },
  {
    text: '3일 전 까지',
    value: '3day',
  },
  {
    text: '날짜 지정',
    value: 'select',
  },
];

interface Props {
  scheduleItem: ScheduleParam;
  endDateFormat: string;
  endDueDate: DateFormat;
  isDisable: boolean;
  handleSetEndDueDate: (changes: Record<string, any>) => void;
  handleSetEndDateFormat: (dateFormat: string) => void;
}

const OptionDueDateFormV2 = (props: Props) => {
  const {
    scheduleItem,
    endDateFormat,
    endDueDate,
    isDisable,
    handleSetEndDueDate,
    handleSetEndDateFormat,
  } = props;

  const selectDateRef = useRef<HTMLInputElement>(null);

  const diff = scheduleItem.term.startedAt - scheduleItem.saleTerm.endedAt;
  const durationObj = dayjs.duration(diff); // 차이를 duration으로 변환

  // 일, 시간, 분 단위로 분리
  const days = Math.floor(durationObj.asDays());
  const hours = durationObj.subtract(days, 'days').hours();

  useEffect(() => {
    if (scheduleItem.saleTerm.startedAt !== 0) {
      handleSetEndDateFormat('select');
      handleSetEndDueDate({
        date: days,
        time: hours,
      });
    }
  }, []);

  const renderContents = (): JSX.Element => {
    if (isDisable) {
      return (
        <Column align="flex-start" gap="16px">
          <Text color="#414B56" size="18px" weight={600}>
            신청 가능 기간
          </Text>
          <Row
            gap="7px"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2,1fr)',
              width: '100%',
            }}
          >
            {DATE_FORMAT_LIST.map(item => (
              <DueDateContainer
                key={item.value}
                $isSelected={endDateFormat === item.value}
                $isDisable={isDisable}
              >
                <Text
                  size="16px"
                  color={endDateFormat === item.value ? '#414B56' : '#c2c4c8'}
                >
                  {item.text}{' '}
                  {item.value === '3day' && (
                    <span style={{ color: '#0071e9' }}>(권장)</span>
                  )}
                </Text>
              </DueDateContainer>
            ))}
          </Row>
          {endDateFormat === 'select' && (
            <SelectDateForm ref={selectDateRef} $isDisable={isDisable}>
              <Text size="12px">선택한 날짜로 부터</Text>
              <Row gap="8px">
                <input
                  style={{
                    width: '25px',
                    border: 'none',
                    textAlign: 'right',
                  }}
                  type="text"
                  placeholder="0"
                  value={endDueDate.date !== 0 ? endDueDate.date : undefined}
                  onFocus={() => selectDateRef.current?.focus()}
                  onBlur={() => selectDateRef.current?.blur()}
                />
                <Text size="14px" weight={400}>
                  일
                </Text>
                <input
                  style={{
                    width: '25px',
                    border: 'none',
                    textAlign: 'right',
                  }}
                  type="text"
                  placeholder="0"
                  value={endDueDate.time !== 0 ? endDueDate.time : undefined}
                  onFocus={() => selectDateRef.current?.focus()}
                  onBlur={() => selectDateRef.current?.blur()}
                />
                <Text size="14px" weight={400}>
                  시간
                </Text>
                <Text color="#1b1c1e" weight={400}>
                  전 까지 판매
                </Text>
              </Row>
            </SelectDateForm>
          )}
        </Column>
      );
    }

    return (
      <Column align="flex-start" gap="12px">
        <Column align="flex-start" gap="16px">
          <Text color="#414B56" size="18px" weight={600}>
            신청 가능 기간
          </Text>
          <Row
            gap="7px"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2,1fr)',
              width: '100%',
            }}
          >
            {DATE_FORMAT_LIST.map(item => (
              <DueDateContainer
                key={item.value}
                $isSelected={endDateFormat === item.value}
                $isDisable={isDisable}
                onClick={() => handleSetEndDateFormat(item.value)}
              >
                <Text
                  size="16px"
                  color={endDateFormat === item.value ? '#414B56' : '#c2c4c8'}
                >
                  {item.text}{' '}
                  {item.value === '3day' && (
                    <span style={{ color: '#0071e9' }}>(권장)</span>
                  )}
                </Text>
              </DueDateContainer>
            ))}
          </Row>
          {endDateFormat === 'select' && (
            <SelectDateForm>
              <Text size="12px">선택한 날짜로 부터</Text>
              <Row gap="8px">
                <input
                  style={{
                    width: '25px',
                    border: 'none',
                    textAlign: 'right',
                  }}
                  type="text"
                  placeholder="0"
                  value={endDueDate.date !== 0 ? endDueDate.date : undefined}
                  onChange={e => {
                    handleSetEndDueDate({
                      date: Number(
                        e.target.value.replaceAll(ONLY_NUMBER_REGEX, ''),
                      ),
                    });
                  }}
                />
                <Text size="14px" weight={400}>
                  일
                </Text>
                <input
                  style={{
                    width: '25px',
                    border: 'none',
                    textAlign: 'right',
                  }}
                  type="text"
                  placeholder="0"
                  value={endDueDate.time !== 0 ? endDueDate.time : undefined}
                  onChange={e => {
                    handleSetEndDueDate({
                      time: Number(
                        e.target.value.replaceAll(ONLY_NUMBER_REGEX, ''),
                      ),
                    });
                  }}
                />
                <Text size="14px" weight={400}>
                  시간
                </Text>
                <Text color="#1b1c1e" weight={400}>
                  전 까지 판매
                </Text>
              </Row>
            </SelectDateForm>
          )}
        </Column>
        <Row gap="6px">
          <CommonImage
            src={ICON_NOTICE_RED}
            style={{ width: '20px', height: '20px' }}
          />
          <Text weight={400} color="#ff4242">
            신청 마감일이 지나면 판매가 종료되고, 최소 인원 충족 여부를 확인해
            일정의 취소 여부를 결정합니다.
          </Text>
        </Row>
      </Column>
    );
  };

  return renderContents();
};

export default OptionDueDateFormV2;
